import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';

interface VanApplication {
  id: string;
  status: string;
  van_tier: string;
  delivery_date: string | null;
}

interface EligibilityStatus {
  isEligible: boolean;
  activeDays: number;
  incomeVerified: boolean;
  accountStanding: string;
  hasInsurance: boolean;
  hasPaymentMethod: boolean;
}

export default function VanAccessModule() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState<EligibilityStatus | null>(null);
  const [application, setApplication] = useState<VanApplication | null>(null);

  useEffect(() => {
    loadVanAccessData();
  }, []);

  const loadVanAccessData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user eligibility data
      const { data: userData } = await supabase
        .from('users')
        .select('provider_active_days, dashboard_income_verified, account_standing, has_valid_insurance, default_payment_method_id')
        .eq('id', user.id)
        .single();

      if (userData) {
        const eligibilityData: EligibilityStatus = {
          isEligible: 
            (userData.provider_active_days || 0) >= 90 &&
            userData.dashboard_income_verified === true &&
            userData.account_standing === 'good' &&
            userData.has_valid_insurance === true &&
            !!userData.default_payment_method_id,
          activeDays: userData.provider_active_days || 0,
          incomeVerified: userData.dashboard_income_verified || false,
          accountStanding: userData.account_standing || 'good',
          hasInsurance: userData.has_valid_insurance || false,
          hasPaymentMethod: !!userData.default_payment_method_id
        };
        setEligibility(eligibilityData);
      }

      // Get existing application
      const { data: appData } = await supabase
        .from('van_applications')
        .select('*')
        .eq('provider_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (appData) {
        setApplication(appData);
      }
    } catch (error) {
      console.error('Error loading van access data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getModuleState = () => {
    if (!eligibility) return 'loading';
    if (!eligibility.isEligible) return 'not_eligible';
    if (!application) return 'eligible';
    
    switch (application.status) {
      case 'submitted':
      case 'approved':
      case 'scheduled':
        return 'in_progress';
      case 'active':
        return 'active';
      case 'payment_issue':
        return 'payment_issue';
      case 'closed':
        return eligibility.isEligible ? 'eligible' : 'not_eligible';
      default:
        return 'eligible';
    }
  };

  const getButtonConfig = () => {
    const state = getModuleState();
    
    switch (state) {
      case 'not_eligible':
        return {
          label: 'View Eligibility',
          disabled: false,
          icon: 'ri-lock-line',
          action: () => navigate('/provider/van-access/eligibility')
        };
      case 'eligible':
        return {
          label: 'Apply for a Van',
          disabled: false,
          icon: 'ri-add-circle-line',
          action: () => navigate('/provider/van-access/apply')
        };
      case 'in_progress':
        return {
          label: 'View Application Status',
          disabled: false,
          icon: 'ri-time-line',
          action: () => navigate('/provider/van-access/status')
        };
      case 'active':
        return {
          label: 'Manage My Van',
          disabled: false,
          icon: 'ri-truck-line',
          action: () => navigate('/provider/van-access/manage')
        };
      case 'payment_issue':
        return {
          label: 'Fix Payment',
          disabled: false,
          icon: 'ri-error-warning-line',
          action: () => navigate('/provider/van-access/payment')
        };
      default:
        return {
          label: 'Loading...',
          disabled: true,
          icon: 'ri-loader-4-line',
          action: () => {}
        };
    }
  };

  const getStatusBadge = () => {
    const state = getModuleState();
    
    switch (state) {
      case 'not_eligible':
        return <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">Locked</span>;
      case 'eligible':
        return <span className="px-3 py-1 bg-teal-50 text-teal-600 text-xs font-medium rounded-full">Available</span>;
      case 'in_progress':
        return <span className="px-3 py-1 bg-amber-50 text-amber-600 text-xs font-medium rounded-full">In Progress</span>;
      case 'active':
        return <span className="px-3 py-1 bg-green-50 text-green-600 text-xs font-medium rounded-full">Active</span>;
      case 'payment_issue':
        return <span className="px-3 py-1 bg-red-50 text-red-600 text-xs font-medium rounded-full">Payment Issue</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  const buttonConfig = getButtonConfig();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center">
            <i className="ri-truck-line text-2xl text-teal-600"></i>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Van Access</h3>
            <p className="text-sm text-gray-500">Get a van for your business</p>
          </div>
        </div>
        {getStatusBadge()}
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Access affordable vans with weekly billing. No credit checks required.
      </p>

      <button
        onClick={buttonConfig.action}
        disabled={buttonConfig.disabled}
        className="w-full px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
      >
        <i className={buttonConfig.icon}></i>
        {buttonConfig.label}
      </button>
    </div>
  );
}
